/**
 * Context to share sync backoff state across components
 * Used by the page-level sync logic and the dev inspector
 */

import * as React from 'react'

export interface SyncBackoffState {
  failureCount: number
  nextSyncTime: number
}

interface SyncBackoffContextValue {
  state: SyncBackoffState
  setState: (state: SyncBackoffState) => void
}

const SyncBackoffContext = React.createContext<SyncBackoffContextValue | null>(null)

export const SyncBackoffProvider: React.FC<{
  children: React.ReactNode
  value: SyncBackoffState
}> = ({ children, value }) => {
  const [state, setState] = React.useState<SyncBackoffState>(value)

  // Update internal state when prop changes
  React.useEffect(() => {
    setState(value)
  }, [value])

  return (
    <SyncBackoffContext.Provider value={{ state, setState }}>
      {children}
    </SyncBackoffContext.Provider>
  )
}

export function useSyncBackoff(): SyncBackoffState {
  const context = React.useContext(SyncBackoffContext)
  if (!context) {
    // Return default values if not in context (e.g., in other pages)
    return { failureCount: 0, nextSyncTime: 0 }
  }
  return context.state
}

export function useSetSyncBackoff(): (state: SyncBackoffState) => void {
  const context = React.useContext(SyncBackoffContext)
  if (!context) {
    return () => {} // No-op if not in context
  }
  return context.setState
}
